import AppSelect from "../../../shared/components/AppSelect";
import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import OrganizerDatePicker from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
} from "../../challenges/types/challengeAdmin";
import AdminVenueFields from "./AdminVenueFields";
import type {
  TournamentCategoryEditorField,
  TournamentCategoryEditorItem,
} from "./TournamentCategoriesEditor";

type Props = {
  modal: {
    mode: "create" | "edit";
    originalCategoryKey: string | null;
    draft: TournamentCategoryEditorItem;
  };
  categoryOptions: ChallengeCategoryCode[];
  genderOptions: ChallengeCategoryGender[];
  onChangeDraft: (field: TournamentCategoryEditorField, value: string) => void;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
  submitLabel?: string;
  categoryLabel: (category: ChallengeCategoryCode) => string;
  categoryGenderLabel: (gender: ChallengeCategoryGender) => string;
  token?: string | null;
  showCategoryTime?: boolean;
  categoryDate?: Date | null;
  categoryTimeMode?: "dateTime" | "timeOnly";
  acceptLegacyPocoTime?: boolean;
  embedded?: boolean;
  lockRegistrationType?: boolean;
  lockSlotLimit?: boolean;
};

const EVENT_TIME_ZONE = "America/Sao_Paulo";

function getZonedDateTimeParts(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  return Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
}

function getDatePart(value?: string | null, acceptLegacyPocoTime = false) {
  const raw = String(value || "").trim();
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const parts = getZonedDateTimeParts(raw);
    if (parts) return `${parts.year}-${parts.month}-${parts.day}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (acceptLegacyPocoTime) {
    const parts = getZonedDateTimeParts(raw);
    if (parts) return `${parts.year}-${parts.month}-${parts.day}`;
  }
  return "";
}

function getTimePart(value?: string | null, acceptLegacyPocoTime = false) {
  const raw = String(value || "").trim();
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const parts = getZonedDateTimeParts(raw);
    if (parts) return `${parts.hour}:${parts.minute}`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) {
    return raw.slice(11, 16);
  }
  if (acceptLegacyPocoTime) {
    const parts = getZonedDateTimeParts(raw);
    if (parts) return `${parts.hour}:${parts.minute}`;
  }
  return "";
}

function combineDateTimeParts(dateValue: string, timeValue: string) {
  const date = String(dateValue || "").trim();
  if (!date) return "";
  const time = String(timeValue || "").trim() || "00:00";
  return `${date}T${time}`;
}

function formatDateForInput(value?: Date | null) {
  if (!value || Number.isNaN(value.getTime())) return "";
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(value.getDate()).padStart(2, "0")}`;
}

export default function TournamentCategoryModal({
  modal,
  categoryOptions,
  genderOptions,
  onChangeDraft,
  onClose,
  onSave,
  saving = false,
  submitLabel,
  categoryLabel,
  categoryGenderLabel,
  token,
  showCategoryTime = false,
  categoryDate = null,
  categoryTimeMode = "dateTime",
  acceptLegacyPocoTime = false,
  embedded = false,
  lockRegistrationType = false,
  lockSlotLimit = false,
}: Props) {
  const t = siteTexts.admin.tournamentCategoryEditor;
  const info = siteTexts.common.info;
  useBodyScrollLock(!embedded);
  const categoryDateValue =
    categoryTimeMode === "timeOnly"
      ? formatDateForInput(categoryDate) ||
        getDatePart(modal.draft.scheduledAt, acceptLegacyPocoTime)
      : getDatePart(modal.draft.scheduledAt, acceptLegacyPocoTime);
  const selectedRegistrationType =
    modal.draft.registrationType === "fixed_pair" ? "fixed_pair" : "individual";
  const categoryTimeMissing =
    showCategoryTime &&
    !getTimePart(modal.draft.scheduledAt, acceptLegacyPocoTime);

  const content = (
    <div
      className={
        embedded
          ? "admin-category-embedded-editor"
          : "admin-modal admin-tournament-modal admin-tournament-category-modal"
      }
    >
      <div className="admin-modal-header">
        <h3>{modal.mode === "edit" ? t.editCategory : t.addCategory}</h3>

        <button
          type="button"
          className="admin-modal-icon-close"
          onClick={onClose}
          aria-label={embedded ? "Voltar para categorias" : siteTexts.common.close}
          title={embedded ? "Voltar para categorias" : siteTexts.common.close}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
      </div>

      <div className="admin-form-grid">
        <label className="field">
          <span>{t.categoryName}</span>
          <input
            type="text"
            value={modal.draft.categoryName}
            onChange={(event) =>
              onChangeDraft("categoryName", event.target.value)
            }
          />
        </label>

        <label className="field">
          <span className="info-label">
            {t.gender}
            <InfoTooltip content={info.openCategory} />
          </span>
          <select
            value={modal.draft.categoryGender || "mixed"}
            onChange={(event) =>
              onChangeDraft("categoryGender", event.target.value)
            }
          >
            {genderOptions.map((gender) => (
              <option key={gender} value={gender}>
                {categoryGenderLabel(gender)}
              </option>
            ))}
          </select>
        </label>

        {["mixed", "open", "male"].includes(
          String(modal.draft.categoryGender || "mixed"),
        ) && (
          <label className="field">
            <span className="info-label">
              {t.boysCategory}
              <InfoTooltip content={info.category} />
            </span>
            <select
              value={modal.draft.categoryMale || "6a"}
              onChange={(event) =>
                onChangeDraft("categoryMale", event.target.value)
              }
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {categoryLabel(category)}
                </option>
              ))}
            </select>
          </label>
        )}

        {["mixed", "open", "female"].includes(
          String(modal.draft.categoryGender || "mixed"),
        ) && (
          <label className="field">
            <span className="info-label">
              {t.girlsCategory}
              <InfoTooltip content={info.category} />
            </span>
            <select
              value={modal.draft.categoryFemale || "6a"}
              onChange={(event) =>
                onChangeDraft("categoryFemale", event.target.value)
              }
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {categoryLabel(category)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span className="info-label">
            {t.registrationMode}
            <InfoTooltip content={info.fixedPair} />
          </span>
          <select
            value={selectedRegistrationType}
            disabled={lockRegistrationType}
            onChange={(event) =>
              onChangeDraft("registrationType", event.target.value)
            }
          >
            <option value="individual">{t.individual}</option>
            <option value="fixed_pair">{t.fixedPair}</option>
          </select>
          <p className="admin-category-registration-mode-hint">
            {selectedRegistrationType === "fixed_pair"
              ? t.fixedPairSlotInfo
              : t.individualSlotInfo}
          </p>
        </label>

        <AdminVenueFields
          token={token}
          mode="address-only"
          address={String(modal.draft.address || "")}
          addressLabel={t.categoryLocation}
          addressPlaceholder={t.categoryLocationPlaceholder}
          onAddressChange={(value) => onChangeDraft("address", value)}
        />

        {showCategoryTime && (
          <div className="admin-row-two">
            {categoryTimeMode !== "timeOnly" && (
              <OrganizerDatePicker
                label={siteTexts.admin.labels.date}
                value={getDatePart(
                  modal.draft.scheduledAt,
                  acceptLegacyPocoTime,
                )}
                onChange={(value) =>
                  onChangeDraft(
                    "scheduledAt",
                    combineDateTimeParts(
                      value,
                      getTimePart(
                        modal.draft.scheduledAt,
                        acceptLegacyPocoTime,
                      ),
                    ),
                  )
                }
              />
            )}
            <OrganizerTimeInput
              label={
                <span className="info-label">
                  {siteTexts.admin.labels.time}
                  <InfoTooltip content={info.categoryTime} />
                </span>
              }
              value={getTimePart(
                modal.draft.scheduledAt,
                acceptLegacyPocoTime,
              )}
              onChange={(value) =>
                onChangeDraft(
                  "scheduledAt",
                  combineDateTimeParts(categoryDateValue, value),
                )
              }
              required
            />
          </div>
        )}

        {lockRegistrationType && <label className="field">
          <span>{siteTexts.admin.createChallengePage.customSettings.categoryCourtsCount}</span>
          <AppSelect value={String(modal.draft.courtsCount || (Number(modal.draft.slotLimit) === 8 ? 4 : 3))} onChange={event => onChangeDraft("courtsCount", event.target.value)}>
            <option value="3">3</option>
            <option value="4">4</option>
          </AppSelect>
        </label>}

        <label className="field">
          <span className="info-label">
            {t.slotsInCategory}
            <InfoTooltip content={info.slotLimit} />
          </span>
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={String(modal.draft.slotLimit || "")}
            disabled={lockSlotLimit}
            readOnly={lockSlotLimit}
            onChange={(event) =>
              lockSlotLimit
                ? undefined
                : onChangeDraft("slotLimit", event.target.value)
            }
          />
        </label>
      </div>

      <div className="admin-modal-actions">
        <Button type="button" variant="pill" onClick={onClose}>
          {siteTexts.common.cancel}
        </Button>

        <Button
          type="button"
          variant="primary"
          onClick={onSave}
          disabled={saving || categoryTimeMissing}
        >
          {saving
            ? siteTexts.admin.historyList.saving
            : submitLabel || siteTexts.common.apply}
        </Button>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div
      className="admin-modal-backdrop admin-nested-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={modal.mode === "edit" ? t.editCategory : t.addCategory}
    >
      {content}
    </div>
  );
}
